<script setup lang="ts">
import { defineProps, ref, watch, computed } from 'vue'
import FaqQuestion from '../FaqQuestion.vue'
import SectionHeader from '../../common/SectionHeader.vue'
import { getStateFullName } from '~/utils/states'
import {
  generateSalaryFAQQuestions
} from '~/utils/salaryFAQ'

const props = defineProps({
  companyName: {
    type: String,
    required: true
  },
  role: {
    type: String,
    default: ''
  },
  state: {
    type: String,
    default: ''
  },
  city: {
    type: String,
    default: ''
  },
  preFetchedData: {
    type: Object,
    default: null
  },
  brandColor: {
    type: String,
    default: '#6b46c1'
  }
})
const faqData = ref(props.preFetchedData)
const loading = ref(!props.preFetchedData)
const error = ref(null)
watch(
  () => props.preFetchedData,
  (newData) => {
    if (newData) {
      faqData.value = newData
      loading.value = false
    }
  }
)
const faqType = computed(() => {
  if (props.role && (props.state || props.city)) {
    return 'role-location'
  } else if (props.role) {
    return 'role'
  } else if (props.state || props.city) {
    return 'location'
  } else {
    return 'company'
  }
})
const location = computed(() => {
  if (props.city && props.state) {
    return `${props.city}, ${getStateFullName(props.state)}`
  } else if (props.state) {
    return getStateFullName(props.state)
  }
  return 'USA'
})
faqData.value.location = location

const faqQuestions = computed(() => {
  if (!faqData.value) return []
  return generateSalaryFAQQuestions(
    props.companyName,
    faqData.value,
    faqType.value as any
  )
})
const titleText = computed(() => {
  if (!faqData.value) return ''
  if (faqType.value === 'company') {
    return `Frequently Asked Questions About Salaries at ${props.companyName}`
  } else if (faqType.value === 'role') {
    return `Frequently Asked Questions About ${faqData.value.keyword} Pay at ${props.companyName}`
  } else if (faqType.value === 'location') {
    return `Frequently Asked Questions About Salaries at ${props.companyName} in ${location.value}`
  } else if (faqType.value === 'role-location') {
    return `Frequently Asked Questions About ${faqData.value.keyword} Pay at ${props.companyName} in ${location.value}`
  }
  return ''
})
</script>

<template>
  <div
    v-if="loading"
    class="py-4"
  >
    <div class="animate-pulse">
      <div class="h-8 w-3/4 bg-gray-200 rounded mb-4" />
      <div class="h-4 w-full bg-gray-200 rounded mb-2" />
      <div class="h-4 w-full bg-gray-200 rounded mb-2" />
      <div class="h-4 w-3/4 bg-gray-200 rounded mb-6" />
      <div class="h-4 w-full bg-gray-200 rounded mb-2" />
      <div class="h-4 w-full bg-gray-200 rounded mb-2" />
      <div class="h-4 w-3/4 bg-gray-200 rounded" />
    </div>
  </div>
  <div
    v-else-if="faqData && faqQuestions.length > 0"
    class="bg-white rounded-lg my-4"
  >
    <SectionHeader
      :title="titleText"
      class="mb-6"
    />
    <div class="space-y-4">
      <FaqQuestion
        v-for="(faq, index) in faqQuestions"
        :key="index"
        :question="faq.question"
        :answers="faq.answers"
        :brand-color="brandColor"
      />
    </div>
  </div>
  <div
    v-else-if="error"
    class="text-red-500 py-4"
  >
    {{ error }}
  </div>
</template>

<style scoped></style>
